import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertTriangle,
    FiCheck,
    FiCopy,
    FiDownload,
    FiEdit3,
    FiEye,
    FiFileText,
    FiMail,
    FiMoreHorizontal,
    FiPrinter,
    FiRefreshCw,
    FiSave,
    FiSend,
    FiShare2,
    FiTrash2,
    FiX,
    FiXCircle,
} from "react-icons/fi";

import "./InvoiceActions.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const DEFAULT_PERMISSIONS = {
    canSaveDraft: true,
    canSave: true,
    canSaveAndPrint: true,
    canPreview: true,
    canPrint: true,
    canDownload: true,
    canShare: true,
    canEmail: true,
    canDuplicate: true,
    canEdit: true,
    canCancel: true,
    canDelete: true,
    canConvert: false,
};

const ACTION_KEYS = {
    SAVE_DRAFT: "SAVE_DRAFT",
    SAVE: "SAVE",
    SAVE_AND_PRINT:
        "SAVE_AND_PRINT",
    PREVIEW: "PREVIEW",
    PRINT: "PRINT",
    DOWNLOAD: "DOWNLOAD",
    SHARE: "SHARE",
    EMAIL: "EMAIL",
    DUPLICATE: "DUPLICATE",
    EDIT: "EDIT",
    CANCEL: "CANCEL",
    DELETE: "DELETE",
    CONVERT: "CONVERT",
};

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const normalizeStatus = (
    value
) => {
    return String(
        value || ""
    )
        .trim()
        .toUpperCase();
};

const getDocumentLabel = (
    documentType
) => {
    switch (
        normalizeStatus(
            documentType
        )
    ) {
        case "QUOTATION":
            return "Quotation";

        case "PROFORMA_INVOICE":
            return "Proforma Invoice";

        case "SALE_ORDER":
            return "Sale Order";

        case "DELIVERY_CHALLAN":
            return "Delivery Challan";

        case "SALE_RETURN":
            return "Sale Return";

        case "PURCHASE_INVOICE":
            return "Purchase Invoice";

        default:
            return "Sale Invoice";
    }
};

const getDefaultPermissions = ({
    documentStatus,
    documentType,
    isEditMode,
}) => {
    const status =
        normalizeStatus(
            documentStatus
        );

    const type =
        normalizeStatus(
            documentType
        );

    const isDraft =
        !status ||
        status ===
            "DRAFT";

    const isConfirmed =
        status ===
        "CONFIRMED";

    const isCancelled =
        status ===
        "CANCELLED";

    const isConverted =
        status ===
        "CONVERTED";

    return {
        ...DEFAULT_PERMISSIONS,

        canSaveDraft:
            isDraft,

        canSave:
            isDraft,

        canSaveAndPrint:
            isDraft ||
            isConfirmed,

        canPreview:
            true,

        canPrint:
            !isDraft ||
            isEditMode,

        canDownload:
            !isDraft ||
            isEditMode,

        canShare:
            !isDraft ||
            isEditMode,

        canEmail:
            !isDraft ||
            isEditMode,

        canDuplicate:
            Boolean(
                isEditMode
            ),

        canEdit:
            isDraft &&
            Boolean(
                isEditMode
            ),

        canCancel:
            isConfirmed,

        canDelete:
            isDraft &&
            Boolean(
                isEditMode
            ),

        canConvert:
            type ===
                "QUOTATION" &&
            !isCancelled &&
            !isConverted,
    };
};

/*
|--------------------------------------------------------------------------
| CONFIRMATION DIALOG
|--------------------------------------------------------------------------
*/

const ActionConfirmationDialog = ({
    open,
    title,
    message,
    confirmLabel,
    cancelLabel =
        "Back",
    destructive =
        false,
    requireReason =
        false,
    reasonLabel =
        "Reason",
    reasonPlaceholder =
        "Enter reason",
    loading =
        false,
    onClose,
    onConfirm,
}) => {
    const [
        reason,
        setReason,
    ] = useState("");

    useEffect(() => {
        if (!open) {
            setReason("");
        }
    }, [open]);

    if (!open) {
        return null;
    }

    const reasonMissing =
        requireReason &&
        !String(
            reason || ""
        ).trim();

    return (
        <div
            className="invoice-action-dialog-backdrop"
            role="presentation"
            onMouseDown={(
                event
            ) => {
                if (
                    event.target ===
                    event.currentTarget &&
                    !loading
                ) {
                    onClose?.();
                }
            }}
        >
            <div
                className="invoice-action-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="invoice-action-dialog-title"
            >
                <div className="invoice-action-dialog-icon">
                    {destructive ? (
                        <FiAlertTriangle />
                    ) : (
                        <FiCheck />
                    )}
                </div>

                <div className="invoice-action-dialog-content">
                    <h3 id="invoice-action-dialog-title">
                        {title}
                    </h3>

                    <p>
                        {message}
                    </p>

                    {requireReason && (
                        <div className="invoice-action-dialog-field">
                            <label>
                                {reasonLabel}
                                <span>
                                    *
                                </span>
                            </label>

                            <textarea
                                rows="4"
                                value={
                                    reason
                                }
                                disabled={
                                    loading
                                }
                                placeholder={
                                    reasonPlaceholder
                                }
                                onChange={(
                                    event
                                ) =>
                                    setReason(
                                        event.target
                                            .value
                                    )
                                }
                            />
                        </div>
                    )}
                </div>

                <div className="invoice-action-dialog-footer">
                    <button
                        type="button"
                        className="invoice-action-dialog-secondary"
                        disabled={
                            loading
                        }
                        onClick={
                            onClose
                        }
                    >
                        {cancelLabel}
                    </button>

                    <button
                        type="button"
                        className={
                            destructive
                                ? "invoice-action-dialog-primary invoice-action-dialog-primary-danger"
                                : "invoice-action-dialog-primary"
                        }
                        disabled={
                            loading ||
                            reasonMissing
                        }
                        onClick={() =>
                            onConfirm?.(
                                reason
                            )
                        }
                    >
                        {loading ? (
                            <>
                                <span className="invoice-actions-spinner" />

                                Processing...
                            </>
                        ) : (
                            confirmLabel
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const InvoiceActions = ({
    documentType =
        "SALE_INVOICE",

    documentStatus =
        "DRAFT",

    isEditMode =
        false,

    disabled =
        false,

    loadingAction =
        "",

    permissions =
        {},

    convertLabel =
        "Convert to Sale Invoice",

    sticky =
        true,

    showKeyboardHints =
        true,

    closeLabel =
        "Cancel",

    onSaveDraft,
    onSave,
    onSaveAndPrint,
    onPreview,
    onPrint,
    onDownload,
    onShare,
    onEmail,
    onDuplicate,
    onEdit,
    onCancel,
    onDelete,
    onConvert,
    onClose,
}) => {
    const [
        moreOpen,
        setMoreOpen,
    ] = useState(false);

    const [
        confirmState,
        setConfirmState,
    ] = useState(null);

    const documentLabel =
        useMemo(
            () =>
                getDocumentLabel(
                    documentType
                ),
            [documentType]
        );

    const resolvedPermissions =
        useMemo(
            () => ({
                ...getDefaultPermissions({
                    documentStatus,
                    documentType,
                    isEditMode,
                }),

                ...permissions,
            }),
            [
                documentStatus,
                documentType,
                isEditMode,
                permissions,
            ]
        );

    const isLoading =
        useCallback(
            (
                actionKey
            ) =>
                normalizeStatus(
                    loadingAction
                ) ===
                actionKey,
            [loadingAction]
        );

    const anyLoading =
        Boolean(
            loadingAction
        );

    const runAction =
        useCallback(
            async (
                actionKey,
                handler,
                payload
            ) => {
                if (
                    disabled ||
                    anyLoading ||
                    typeof handler !==
                        "function"
                ) {
                    return;
                }

                setMoreOpen(
                    false
                );

                await handler(
                    payload
                );
            },
            [
                anyLoading,
                disabled,
            ]
        );

    const openConfirmation =
        useCallback(
            (
                config
            ) => {
                setMoreOpen(
                    false
                );

                setConfirmState(
                    config
                );
            },
            []
        );

    const closeConfirmation =
        useCallback(() => {
            if (
                anyLoading
            ) {
                return;
            }

            setConfirmState(
                null
            );
        }, [
            anyLoading,
        ]);

    const confirmAction =
        useCallback(
            async (
                reason
            ) => {
                if (
                    !confirmState
                ) {
                    return;
                }

                await runAction(
                    confirmState
                        .actionKey,
                    confirmState
                        .handler,
                    {
                        reason,
                    }
                );

                setConfirmState(
                    null
                );
            },
            [
                confirmState,
                runAction,
            ]
        );

    useEffect(() => {
        const handleShortcut = (
            event
        ) => {
            if (
                disabled ||
                anyLoading
            ) {
                return;
            }

            const target =
                event.target;

            const tagName =
                String(
                    target?.tagName ||
                    ""
                ).toUpperCase();

            const isTextField =
                [
                    "INPUT",
                    "TEXTAREA",
                    "SELECT",
                ].includes(
                    tagName
                );

            if (
                event.ctrlKey &&
                event.key
                    .toLowerCase() ===
                    "s"
            ) {
                event.preventDefault();

                if (
                    event.shiftKey &&
                    resolvedPermissions
                        .canSaveDraft
                ) {
                    runAction(
                        ACTION_KEYS
                            .SAVE_DRAFT,
                        onSaveDraft
                    );
                } else if (
                    resolvedPermissions
                        .canSave
                ) {
                    runAction(
                        ACTION_KEYS
                            .SAVE,
                        onSave
                    );
                }

                return;
            }

            if (
                event.ctrlKey &&
                event.key
                    .toLowerCase() ===
                    "p"
            ) {
                event.preventDefault();

                if (
                    resolvedPermissions
                        .canPrint
                ) {
                    runAction(
                        ACTION_KEYS
                            .PRINT,
                        onPrint
                    );
                }

                return;
            }

            if (
                !isTextField &&
                event.key ===
                    "Escape"
            ) {
                setMoreOpen(
                    false
                );

                onClose?.();
            }
        };

        window.addEventListener(
            "keydown",
            handleShortcut
        );

        return () => {
            window.removeEventListener(
                "keydown",
                handleShortcut
            );
        };
    }, [
        anyLoading,
        disabled,
        onClose,
        onPrint,
        onSave,
        onSaveDraft,
        resolvedPermissions,
        runAction,
    ]);

    return (
        <>
            {
                sticky &&
                (
                    <div
                        className="invoice-actions-fixed-spacer"
                        aria-hidden="true"
                    />
                )
            }


            <div
                className={
                    [
                        "invoice-actions",
                        sticky
                            ? "invoice-actions-sticky"
                            : "",
                        `invoice-actions-status-${normalizeStatus(
                            documentStatus
                        ).toLowerCase() || "draft"}`,
                    ]
                        .filter(Boolean)
                        .join(" ")
                }
                data-document-type={
                    normalizeStatus(
                        documentType
                    )
                }
                data-document-status={
                    normalizeStatus(
                        documentStatus
                    )
                }
            >
                <div className="invoice-actions-left">
                    <button
                        type="button"
                        className="invoice-actions-close-button"
                        disabled={
                            disabled ||
                            anyLoading
                        }
                        onClick={
                            onClose
                        }
                    >
                        <FiX />

                        {closeLabel}
                    </button>

                    {resolvedPermissions
                        .canPreview && (
                        <button
                            type="button"
                            className="invoice-actions-secondary-button"
                            disabled={
                                disabled ||
                                anyLoading
                            }
                            onClick={() =>
                                runAction(
                                    ACTION_KEYS
                                        .PREVIEW,
                                    onPreview
                                )
                            }
                        >
                            {isLoading(
                                ACTION_KEYS
                                    .PREVIEW
                            ) ? (
                                <span className="invoice-actions-spinner" />
                            ) : (
                                <FiEye />
                            )}

                            Preview
                        </button>
                    )}
                </div>

                <div className="invoice-actions-right">
                    {resolvedPermissions
                        .canSaveDraft && (
                        <button
                            type="button"
                            className="invoice-actions-secondary-button"
                            disabled={
                                disabled ||
                                anyLoading
                            }
                            onClick={() =>
                                runAction(
                                    ACTION_KEYS
                                        .SAVE_DRAFT,
                                    onSaveDraft
                                )
                            }
                        >
                            {isLoading(
                                ACTION_KEYS
                                    .SAVE_DRAFT
                            ) ? (
                                <span className="invoice-actions-spinner" />
                            ) : (
                                <FiFileText />
                            )}

                            Save Draft

                            {showKeyboardHints && (
                                <kbd>
                                    Ctrl+Shift+S
                                </kbd>
                            )}
                        </button>
                    )}

                    {resolvedPermissions
                        .canSaveAndPrint && (
                        <button
                            type="button"
                            className="invoice-actions-secondary-button"
                            disabled={
                                disabled ||
                                anyLoading
                            }
                            onClick={() =>
                                runAction(
                                    ACTION_KEYS
                                        .SAVE_AND_PRINT,
                                    onSaveAndPrint
                                )
                            }
                        >
                            {isLoading(
                                ACTION_KEYS
                                    .SAVE_AND_PRINT
                            ) ? (
                                <span className="invoice-actions-spinner" />
                            ) : (
                                <FiPrinter />
                            )}

                            Save & Print
                        </button>
                    )}

                    {resolvedPermissions
                        .canSave && (
                        <button
                            type="button"
                            className="invoice-actions-primary-button"
                            disabled={
                                disabled ||
                                anyLoading
                            }
                            onClick={() =>
                                runAction(
                                    ACTION_KEYS
                                        .SAVE,
                                    onSave
                                )
                            }
                        >
                            {isLoading(
                                ACTION_KEYS
                                    .SAVE
                            ) ? (
                                <span className="invoice-actions-spinner invoice-actions-spinner-light" />
                            ) : (
                                <FiSave />
                            )}

                            Save {documentLabel}

                            {showKeyboardHints && (
                                <kbd>
                                    Ctrl+S
                                </kbd>
                            )}
                        </button>
                    )}

                    <div className="invoice-actions-more">
                        <button
                            type="button"
                            className="invoice-actions-more-button"
                            aria-label="More actions"
                            disabled={
                                disabled ||
                                anyLoading
                            }
                            onClick={() =>
                                setMoreOpen(
                                    (
                                        previous
                                    ) =>
                                        !previous
                                )
                            }
                        >
                            <FiMoreHorizontal />
                        </button>

                        {moreOpen && (
                            <div className="invoice-actions-menu">
                                {resolvedPermissions
                                    .canEdit && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            runAction(
                                                ACTION_KEYS
                                                    .EDIT,
                                                onEdit
                                            )
                                        }
                                    >
                                        <FiEdit3 />

                                        Edit
                                    </button>
                                )}

                                {resolvedPermissions
                                    .canPrint && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            runAction(
                                                ACTION_KEYS
                                                    .PRINT,
                                                onPrint
                                            )
                                        }
                                    >
                                        <FiPrinter />

                                        Print

                                        {showKeyboardHints && (
                                            <kbd>
                                                Ctrl+P
                                            </kbd>
                                        )}
                                    </button>
                                )}

                                {resolvedPermissions
                                    .canDownload && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            runAction(
                                                ACTION_KEYS
                                                    .DOWNLOAD,
                                                onDownload
                                            )
                                        }
                                    >
                                        <FiDownload />

                                        Download PDF
                                    </button>
                                )}

                                {resolvedPermissions
                                    .canShare && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            runAction(
                                                ACTION_KEYS
                                                    .SHARE,
                                                onShare
                                            )
                                        }
                                    >
                                        <FiShare2 />

                                        Share
                                    </button>
                                )}

                                {resolvedPermissions
                                    .canEmail && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            runAction(
                                                ACTION_KEYS
                                                    .EMAIL,
                                                onEmail
                                            )
                                        }
                                    >
                                        <FiMail />

                                        Email
                                    </button>
                                )}

                                {resolvedPermissions
                                    .canDuplicate && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            runAction(
                                                ACTION_KEYS
                                                    .DUPLICATE,
                                                onDuplicate
                                            )
                                        }
                                    >
                                        <FiCopy />

                                        Duplicate
                                    </button>
                                )}

                                {resolvedPermissions
                                    .canConvert && (
                                    <>
                                        <div className="invoice-actions-menu-divider" />

                                        <button
                                            type="button"
                                            className="invoice-actions-menu-convert"
                                            onClick={() =>
                                                openConfirmation({
                                                    actionKey:
                                                        ACTION_KEYS
                                                            .CONVERT,

                                                    handler:
                                                        onConvert,

                                                    title:
                                                        `Convert ${documentLabel}`,

                                                    message:
                                                        `This will create a new sale invoice using the current ${documentLabel.toLowerCase()} details.`,

                                                    confirmLabel:
                                                        convertLabel,

                                                    destructive:
                                                        false,

                                                    requireReason:
                                                        false,
                                                })
                                            }
                                        >
                                            <FiSend />

                                            {convertLabel}
                                        </button>
                                    </>
                                )}

                                {(resolvedPermissions
                                    .canCancel ||
                                    resolvedPermissions
                                        .canDelete) && (
                                    <div className="invoice-actions-menu-divider" />
                                )}

                                {resolvedPermissions
                                    .canCancel && (
                                    <button
                                        type="button"
                                        className="invoice-actions-menu-danger"
                                        onClick={() =>
                                            openConfirmation({
                                                actionKey:
                                                    ACTION_KEYS
                                                        .CANCEL,

                                                handler:
                                                    onCancel,

                                                title:
                                                    `Cancel ${documentLabel}`,

                                                message:
                                                    `Cancelling this ${documentLabel.toLowerCase()} may reverse related stock and payment allocations. This action should only be used when the document is no longer valid.`,

                                                confirmLabel:
                                                    `Cancel ${documentLabel}`,

                                                destructive:
                                                    true,

                                                requireReason:
                                                    true,

                                                reasonLabel:
                                                    "Cancellation Reason",

                                                reasonPlaceholder:
                                                    "Explain why this document is being cancelled",
                                            })
                                        }
                                    >
                                        <FiXCircle />

                                        Cancel
                                    </button>
                                )}

                                {resolvedPermissions
                                    .canDelete && (
                                    <button
                                        type="button"
                                        className="invoice-actions-menu-danger"
                                        onClick={() =>
                                            openConfirmation({
                                                actionKey:
                                                    ACTION_KEYS
                                                        .DELETE,

                                                handler:
                                                    onDelete,

                                                title:
                                                    `Delete Draft ${documentLabel}`,

                                                message:
                                                    `This draft ${documentLabel.toLowerCase()} will be permanently removed from the active billing list.`,

                                                confirmLabel:
                                                    "Delete Draft",

                                                destructive:
                                                    true,

                                                requireReason:
                                                    false,
                                            })
                                        }
                                    >
                                        <FiTrash2 />

                                        Delete Draft
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ActionConfirmationDialog
                open={
                    Boolean(
                        confirmState
                    )
                }
                title={
                    confirmState
                        ?.title
                }
                message={
                    confirmState
                        ?.message
                }
                confirmLabel={
                    confirmState
                        ?.confirmLabel
                }
                cancelLabel={
                    confirmState
                        ?.cancelLabel
                }
                destructive={
                    confirmState
                        ?.destructive
                }
                requireReason={
                    confirmState
                        ?.requireReason
                }
                reasonLabel={
                    confirmState
                        ?.reasonLabel
                }
                reasonPlaceholder={
                    confirmState
                        ?.reasonPlaceholder
                }
                loading={
                    Boolean(
                        confirmState &&
                        isLoading(
                            confirmState
                                .actionKey
                        )
                    )
                }
                onClose={
                    closeConfirmation
                }
                onConfirm={
                    confirmAction
                }
            />
        </>
    );
};

export {
    ACTION_KEYS,
    DEFAULT_PERMISSIONS,
    getDocumentLabel,
    getDefaultPermissions,
    ActionConfirmationDialog,
};

export default InvoiceActions;
